package co.maytes.standupbot.dashboard.features.standups

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.motion.rotatesWhenOpen
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Popover
import co.maytes.standupbot.dashboard.components.widgets.PopoverPlacement
import co.maytes.standupbot.dashboard.components.widgets.TextInputStyle
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideChevronDown
import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.shared.dto.slack.SlackChannelDto
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Shadow
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCheck
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaHidden
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onKeyDown
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.tabIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input

@Composable
fun ChannelPicker(
    workspaceId: String?,
    selectedId: String?,
    selectedName: String?,
    onChange: (id: String, name: String) -> Unit,
    ariaLabel: String = "Select channel",
    injectedChannels: List<SlackChannelDto>? = null,
) {
    val api = rememberApiClient()
    var fetchedChannels by remember { mutableStateOf<List<SlackChannelDto>?>(null) }
    var reloadTick by remember { mutableStateOf(0) }
    var query by remember { mutableStateOf("") }
    var open by remember { mutableStateOf(false) }
    var loadError by remember { mutableStateOf(false) }

    LaunchedEffect(workspaceId, injectedChannels != null, reloadTick) {
        if (injectedChannels != null) return@LaunchedEffect
        if (workspaceId.isNullOrBlank()) return@LaunchedEffect
        loadError = false
        fetchedChannels = null
        when (val res = api.get<List<SlackChannelDto>>("/api/v1/workspaces/$workspaceId/slack/channels")) {
            is Result.Ok -> fetchedChannels = res.value
            is Result.Err -> loadError = true
        }
    }

    val channels = injectedChannels ?: fetchedChannels

    if (loadError && injectedChannels == null) {
        SlackDidNotAnswerHint(onRetry = { reloadTick++ })
        return
    }

    if (channels != null && channels.isEmpty()) {
        SlackNotConnectedHint()
        return
    }

    val filtered = remember(channels, query) {
        val list = channels.orEmpty()
        if (query.isBlank()) list else list.filter { it.name.contains(query.trim(), ignoreCase = true) }
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .position(Position.Relative),
    ) {
        Column(modifier = Modifier.fillMaxWidth().gap(Spacing.xxs)) {
            SelectedPill(
                selectedId = selectedId,
                selectedName = selectedName,
                isOpen = open,
                ariaLabel = ariaLabel,
                onToggle = { open = !open },
            )
            channels?.firstOrNull { it.id == selectedId }?.let { MembershipNote(it) }
            Popover(
                open = open,
                placement = PopoverPlacement.BottomFill,
                onDismiss = { open = false },
                modifier = Modifier
                    .backgroundColor(Palette.surface)
                    .borderRadius(Radius.md)
                    .styleModifier {
                        property("border", "1px solid ${Palette.border.rgbString()}")
                        property("box-shadow", Shadow.MD)
                    },
            ) {
                Column(modifier = Modifier.fillMaxWidth()) {
                    Input(
                        type = InputType.Text,
                        attrs = TextInputStyle.toModifier()
                            .margin(Spacing.xs)
                            .fillMaxWidth()
                            .toAttrs {
                                placeholder("Search channels…")
                                value(query)
                                onInput { query = it.value }
                            },
                    )
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .maxHeight(DROPDOWN_MAX_HEIGHT.px)
                            .overflow(com.varabyte.kobweb.compose.css.Overflow.Auto),
                    ) {
                        if (filtered.isEmpty()) {
                            SpanText(
                                text = "No channels match \"$query\"",
                                modifier = Modifier
                                    .padding(Spacing.sm)
                                    .fontSize(Typography.bodySmall.size)
                                    .color(Palette.textSecondary),
                            )
                        } else {
                            filtered.forEach { ch ->
                                ChannelOption(channel = ch, selected = ch.id == selectedId) {
                                    onChange(ch.id, ch.name)
                                    open = false
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

private const val DROPDOWN_MAX_HEIGHT = 280

@Composable
private fun SelectedPill(
    selectedId: String?,
    selectedName: String?,
    isOpen: Boolean,
    ariaLabel: String,
    onToggle: () -> Unit,
) {
    val label = when {
        !selectedName.isNullOrBlank() -> "#$selectedName"
        !selectedId.isNullOrBlank() -> selectedId
        else -> "Select channel"
    }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(SELECT_HEIGHT.px)
            .backgroundColor(Palette.surface)
            .color(Palette.textPrimary)
            .borderRadius(Radius.md)
            .padding(leftRight = Spacing.sm)
            .cursor(Cursor.Pointer)
            .role("button")
            .tabIndex(0)
            .ariaLabel(ariaLabel)
            .attrsModifier { attr("aria-expanded", isOpen.toString()) }
            .styleModifier {
                property("border", "1px solid ${if (isOpen) Palette.accent.rgbString() else Palette.border.rgbString()}")
                property("transition", Motion.TRANSITION_BASE)
                property("user-select", "none")
            }
            .onClick { onToggle() }
            .onKeyDown { e ->
                if (e.key == "Enter" || e.key == " ") {
                    e.preventDefault()
                    onToggle()
                }
            },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.body.size)
                .fontWeight(500)
                .color(if (selectedId.isNullOrBlank()) Palette.textTertiary else Palette.textPrimary),
        )
        Box(
            modifier = Modifier
                .ariaHidden()
                .color(Palette.textTertiary)
                .rotatesWhenOpen(isOpen),
        ) {
            LucideChevronDown(size = CHEVRON_SIZE)
        }
    }
}

private const val SELECT_HEIGHT = 40
private const val CHEVRON_SIZE = 16

@Composable
private fun ChannelOption(
    channel: SlackChannelDto,
    selected: Boolean,
    onClick: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(topBottom = Spacing.xs, leftRight = Spacing.sm)
            .cursor(Cursor.Pointer)
            .backgroundColor(if (selected) Palette.accentSubtle else Palette.surface)
            .role("option")
            .tabIndex(0)
            .ariaLabel(channel.name)
            .attrsModifier { attr("aria-selected", selected.toString()) }
            .styleModifier { property("transition", Motion.TRANSITION_BASE) }
            .onClick { onClick() }
            .onKeyDown { e ->
                if (e.key == "Enter" || e.key == " ") {
                    e.preventDefault()
                    onClick()
                }
            },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(Spacing.xs)) {
            SpanText(
                text = if (channel.isPrivate) "🔒" else "#",
                modifier = Modifier
                    .ariaHidden()
                    .fontSize(Typography.body.size)
                    .color(Palette.textTertiary),
            )
            SpanText(
                text = channel.name,
                modifier = Modifier
                    .fontFamily(*Typography.SANS_STACK)
                    .fontSize(Typography.body.size)
                    .color(Palette.textPrimary),
            )
            if (!channel.isMember) {
                SpanText(
                    text = "bot not joined",
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.textTertiary),
                )
            }
        }
        if (selected) {
            LucideCheck(size = 16, strokeColor = Palette.accent.cssRgb())
        }
    }
}

@Composable
private fun MembershipNote(channel: SlackChannelDto) {
    if (channel.isMember) return
    SpanText(
        text = if (channel.isPrivate) {
            "StandupBot isn't in this private channel yet. In Slack, type /invite @StandupBot there, then pick the channel again."
        } else {
            "StandupBot can still post here, but won't see joins and leaves until you /invite it."
        },
        modifier = Modifier
            .fontSize(Typography.bodySmall.size)
            .color(if (channel.isPrivate) Palette.warning else Palette.textSecondary)
            .role("note")
            .styleModifier { property("line-height", "1.6") },
    )
}

@Composable
fun SlackDidNotAnswerHint(onRetry: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(Spacing.sm)
            .backgroundColor(Palette.accentSubtle)
            .borderRadius(Radius.md)
            .role("alert"),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        SpanText(
            text = "Slack didn't answer. Try again.",
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textSecondary),
        )
        Button(label = "Retry", variant = ButtonVariant.Secondary, size = ButtonSize.Sm, onClick = onRetry)
    }
}

@Composable
private fun SlackNotConnectedHint() {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(Spacing.sm)
            .backgroundColor(Palette.accentSubtle)
            .borderRadius(Radius.md),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text = "No Slack workspace connected.",
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textSecondary)
                .margin(right = Spacing.xs),
        )
        Link(
            path = "/app/settings/integrations",
            text = "Connect Slack first →",
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.accent)
                .fontWeight(500),
        )
    }
}

@Suppress("unused")
private fun referencePercent() = 100.percent

@Suppress("unused")
private fun referenceClient(c: ApiClient) = c.hashCode()
